import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Logo } from './Logo';
import { PlanButton } from './PlanCard';
import { PlanErrorSummary } from './PlanErrorSummary';
import { clearCachedProfile } from './PrivateRoute';
import { PLAN_DETAILS, formatBillingDate } from '../utils/planPricing';
import { billingErrorMessage, getCheckoutStatus } from '../utils/planActions';
import { isOnboardingInProgress } from '../utils/signupFlow';

// Where Stripe Checkout sends people after paying: /plans/subscribed?session_id=…
// The server re-fetches the subscription and applies it itself when asked, so this normally
// confirms on the first try; it keeps asking for ~20 seconds in case Stripe is slow.
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

const linkClass =
  'mpg-focus-ring inline-flex min-h-[44px] items-center rounded-md text-base font-semibold text-[color:var(--plan-solo-text)] underline underline-offset-4 hover:text-[color:var(--plan-heading-text)]';

export function CheckoutReturn() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') || '';
  const navigate = useNavigate();
  // checking | complete | slow | not-completed | error | missing
  const [state, setState] = useState(sessionId ? 'checking' : 'missing');
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [attemptRun, setAttemptRun] = useState(0);
  const headingRef = useRef(null);

  const check = useCallback(() => {
    setError('');
    setState('checking');
    setAttemptRun((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let timer = null;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const result = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (result.state === 'complete') {
          // Access has changed: every guarded screen must re-read the account.
          clearCachedProfile();
          setProfile(result.profile || null);
          setState('complete');
          return;
        }
        if (result.state === 'open' || result.state === 'expired') {
          setState('not-completed');
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState('slow');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to confirm checkout:', err);
        setError(billingErrorMessage(err, "We couldn't confirm your subscription yet. Please try again."));
        setState('error');
      }
    };
    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, attemptRun]);

  // Move focus to the new heading whenever the outcome changes, so screen reader users hear it.
  useEffect(() => {
    if (state !== 'checking') headingRef.current?.focus();
  }, [state]);

  const continueSetup = isOnboardingInProgress();
  const goOn = () => navigate(continueSetup ? '/onboarding/start-day' : '/MealPlan', { replace: true });

  const plan = PLAN_DETAILS[profile?.subscriptionPlan]?.name || 'your';
  const interval = profile?.subscriptionInterval === 'yearly' ? 'yearly' : 'monthly';
  const onTrial = (profile?.subscriptionTier || '').toLowerCase() === 'trial';
  const startDate = formatBillingDate(profile?.trialEndDate ? new Date(profile.trialEndDate) : null);
  const renewalDate = formatBillingDate(profile?.subscriptionRenewalDate);

  const heading = {
    checking: 'Confirming your subscription…',
    complete: "You're subscribed",
    slow: 'Almost there',
    'not-completed': "Checkout wasn't completed",
    error: "We couldn't confirm your subscription",
    missing: 'Nothing to confirm',
  }[state];

  return (
    <div className="mpg-focus-scope min-h-screen bg-[var(--plan-page-bg)] px-4 py-10 sm:px-6">
      <main className="mx-auto flex w-full max-w-xl flex-col items-center text-center">
        <div aria-hidden="true">
          <Logo className="mx-auto h-14 w-14" color="#AFBB8B" />
        </div>

        <section className="mt-8 w-full rounded-3xl border-2 border-[color:var(--plan-current-border)] bg-white p-6 text-left sm:p-8">
          {state === 'complete' && (
            <CheckCircle2 className="mb-3 h-10 w-10 text-[#1F5D38]" aria-hidden="true" />
          )}
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-3xl font-bold text-[color:var(--plan-heading-text)] outline-none"
          >
            {heading}
          </h1>

          <div className="mt-4 space-y-4 text-base leading-7 text-[color:var(--plan-heading-text)]" role="status">
            {state === 'checking' && <p>This only takes a moment.</p>}

            {state === 'complete' && onTrial && (
              <p>
                Your {plan} {interval} plan starts{startDate ? ` on ${startDate}` : ''}, when your free trial ends.
                Nothing is charged until then, and you have full access to every feature in the meantime.
              </p>
            )}
            {state === 'complete' && !onTrial && (
              <p>
                Welcome to {plan}. Your plan is active now{renewalDate ? ` and renews on ${renewalDate}` : ''}.
              </p>
            )}

            {state === 'slow' && (
              <p>
                We&apos;re still confirming your payment with Stripe. This usually takes a few seconds. If you were
                charged, your plan will appear shortly.
              </p>
            )}
            {state === 'not-completed' && <p>Nothing was charged. You can choose a plan whenever you&apos;re ready.</p>}
            {state === 'missing' && <p>There&apos;s no checkout to confirm here.</p>}
          </div>

          {state === 'error' && (
            <div className="mt-4">
              <PlanErrorSummary message={error} title="Something needs your attention" />
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3">
            {state === 'complete' && (
              <>
                <PlanButton onClick={goOn}>{continueSetup ? 'Continue setting up' : 'Go to meal planner'}</PlanButton>
                <Link to="/plans" className={linkClass}>View Plan &amp; billing</Link>
              </>
            )}
            {(state === 'slow' || state === 'error') && (
              <>
                <PlanButton onClick={check}>Check again</PlanButton>
                <Link to="/plans" className={linkClass}>Go to Plan &amp; billing</Link>
              </>
            )}
            {(state === 'not-completed' || state === 'missing') && (
              <Link to="/plans" className={linkClass}>Go to Plan &amp; billing</Link>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default CheckoutReturn;
